import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { ModalsWrapperContext } from '@kanda-libs/ks-component-ts';

import useOverrideJob from 'hooks/useOverrideJob';
import useReloadInitialData from 'hooks/useReloadInitialData';
import useSendConsentComms from 'hooks/useSendConsentComms';
import paymentSelectors from 'store/selectors/payments';

import { PROPENSIO_XID_MODAL_ID } from './constants';

export type PropensioCallback =
  | 'approve'
  | 'decline'
  | 'refer'
  | 'cancel'
  | 'action';

export interface PropensioCallbackProps {
  propensioCallback: PropensioCallback | undefined;
  setPropensioCallback: React.Dispatch<
    React.SetStateAction<PropensioCallback | undefined>
  >;
}

export interface PaymentOverrideButtonsHook extends PropensioCallbackProps {
  showAccept: boolean;
  showDecline: boolean;
  showCancel: boolean;
  showReferred: boolean;
  showCustomerAction: boolean;
  showConsent: boolean;
  isSubmitting: boolean;
  acceptClick: () => void;
  declineClick: () => void;
  cancelClick: () => void;
  referredClick: () => void;
  customerActionClick: () => void;
  consentClick: () => void;
}

export default function usePaymentOverrideButtons(): PaymentOverrideButtonsHook {
  const refreshAll = useReloadInitialData();
  const job = useSelector(paymentSelectors.getPaymentJob);
  const credit = useSelector(paymentSelectors.getPaymentCredit);

  const provider = credit?.provider;

  const { showModal } = useContext(ModalsWrapperContext);

  const [propensioCallback, setPropensioCallback] = useState<
    PropensioCallback | undefined
  >();

  const { isSubmitting: overrideJobIsSubmitting, overrideJob } =
    useOverrideJob();

  const { isSubmitting: sendConsentCommsIsSubmitting, sendConsentComms } =
    useSendConsentComms();

  const isSubmitting = overrideJobIsSubmitting || sendConsentCommsIsSubmitting;

  const showAccept = useMemo(() => {
    if (!job) return false;
    return (
      job?.status === 'sent' && job?.finance_status === 'applied_for_finance'
    );
  }, [job]);

  const showReferred = useMemo(() => {
    if (!job) return false;
    return (
      job?.status === 'sent' && job?.finance_status === 'applied_for_finance'
    );
  }, [job]);

  const showCustomerAction = useMemo(() => {
    if (!job) return false;
    return (
      job?.status === 'sent' && job?.finance_status === 'applied_for_finance'
    );
  }, [job]);

  const showDecline = useMemo(() => {
    if (!job) return false;
    return (
      job?.status === 'sent' && job?.finance_status === 'applied_for_finance'
    );
  }, [job]);

  const showCancel = useMemo(() => {
    if (!job) return false;
    if (job?.finance_status === 'not_applied' || job?.finance_status === 'finance_cancelled') return false;
    return true;
  }, [job]);

  const showConsent = useMemo(() => {
    if (!credit) return false;
    const { provider } = credit;
    if (!provider) return false;
    return provider === 'omni';
  }, [credit]);

  const acceptClick = useCallback(() => {
    if (!job?.id || !credit || !provider) return;
    if (provider === 'propensio') {
      setPropensioCallback('approve');
      showModal(PROPENSIO_XID_MODAL_ID);
      return;
    }
    overrideJob(job?.id, '', 'approve', refreshAll);
    refreshAll();
  }, [job, credit, overrideJob, refreshAll]);

  const referredClick = useCallback(() => {
    if (!job?.id || !credit || !provider) return;
    if (provider === 'propensio') {
      setPropensioCallback('refer');
      showModal(PROPENSIO_XID_MODAL_ID);
      return;
    }
    overrideJob(job?.id, '', 'refer', refreshAll);
    refreshAll();
  }, [job, overrideJob, refreshAll]);

  const customerActionClick = useCallback(() => {
    if (!job?.id || !credit || !provider) return;
    if (provider === 'propensio') {
      setPropensioCallback('action');
      showModal(PROPENSIO_XID_MODAL_ID);
      return;
    }
    overrideJob(job?.id, '', 'action', refreshAll);
    refreshAll();
  }, [job, overrideJob, refreshAll]);

  const declineClick = useCallback(() => {
    if (!job?.id || !credit || !provider) return;
    if (provider === 'propensio') {
      setPropensioCallback('decline');
      showModal(PROPENSIO_XID_MODAL_ID);
      return;
    }
    overrideJob(job?.id, '', 'decline', refreshAll);
    refreshAll();
  }, [job, overrideJob, refreshAll]);

  const cancelClick = useCallback(() => {
    if (!job?.id || !credit || !provider) return;
    if (provider === 'propensio') {
      setPropensioCallback('cancel');
      showModal(PROPENSIO_XID_MODAL_ID);
      return;
    }
    overrideJob(job?.id, '', 'cancel', refreshAll);
    refreshAll();
  }, [job, overrideJob, refreshAll]);

  const consentClick = useCallback(() => {
    if (!job?.id || !credit || !provider) return;
    if (provider !== 'omni') return;
    const id = credit?.id;
    if (!id) return;
    sendConsentComms(id);
  }, [job, credit, provider, sendConsentComms]);

  return {
    showAccept,
    showDecline,
    showCancel,
    showReferred,
    showCustomerAction,
    showConsent,
    isSubmitting,
    acceptClick,
    declineClick,
    cancelClick,
    referredClick,
    propensioCallback,
    setPropensioCallback,
    customerActionClick,
    consentClick,
  };
}
